import type { CommandItem } from "./commandItem";

/** Points a query word earns, by where it matches; the item's score is the sum over the words. */
const SCORE_LABEL_START = 4;
const SCORE_LABEL_WORD_START = 3;
const SCORE_LABEL_ANYWHERE = 2;
const SCORE_TEXT_WORD_START = 1.5;
const SCORE_TEXT_ANYWHERE = 1;
/** Some word matches nowhere: the item is left out. */
const NO_MATCH = 0;

function normalize(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** A word starts at the beginning, after a space, after "›" (breadcrumbs) or after "/". */
function wordStartPattern(word: string): RegExp {
  return new RegExp(`(^|[\\s›/])${escapeRegExp(word)}`);
}

function wordScore(word: string, label: string, text: string): number {
  const wordStart = wordStartPattern(word);
  if (label.startsWith(word)) return SCORE_LABEL_START;
  if (wordStart.test(label)) return SCORE_LABEL_WORD_START;
  if (label.includes(word)) return SCORE_LABEL_ANYWHERE;
  if (wordStart.test(text)) return SCORE_TEXT_WORD_START;
  if (text.includes(word)) return SCORE_TEXT_ANYWHERE;
  return NO_MATCH;
}

function itemScore(item: CommandItem, words: string[]): number {
  const label = normalize(item.label);
  const text = normalize([item.label, item.group, item.hint ?? "", ...(item.keywords ?? [])].join(" "));
  let score = 0;
  for (const word of words) {
    const points = wordScore(word, label, text);
    if (points === NO_MATCH) return NO_MATCH;
    score += points;
  }
  return score;
}

/** Ranks items by how well every word of the query matches (label start > word start > anywhere). */
export function filterCommands(items: CommandItem[], query: string): CommandItem[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return items;
  return items
    .map((item, index) => ({ item, index, score: itemScore(item, words) }))
    .filter((ranked) => ranked.score !== NO_MATCH)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((ranked) => ranked.item);
}
